import { useNavigate } from 'react-router-dom';

import Button from '../components/ui/Button';
import Card, { CardContent, CardFooter, CardHeader, CardTitle } from '../components/ui/Card';
import { usePageTitle } from '../hooks/shared';

export default function Pricing() {
  usePageTitle('Pricing');
  const navigate = useNavigate();

  const plans = [
    {
      name: 'Free',
      price: '$0',
      period: 'forever',
      description: 'Essential voter information tools',
      features: [
        'Polling location finder',
        'Basic ballot information',
        'Email election reminders',
        'Voter registration assistance',
        'Account dashboard',
      ],
      cta: 'Get Started',
      ctaVariant: 'outline',
      popular: false,
    },
    {
      name: 'Monthly',
      price: '$4.99',
      period: 'per month',
      description: 'Full-featured voter engagement',
      features: [
        'Everything in Free',
        'Physical mail voter guides',
        'Mail polling location maps',
        'Mail voter registration forms',
        'Detailed candidate information',
        'Priority email support',
        'Advanced reminder options',
      ],
      cta: 'Start Free Trial',
      ctaVariant: 'primary',
      popular: true,
    },
    {
      name: 'Annual',
      price: '$49.99',
      period: 'per year',
      description: 'Best value for engaged voters',
      features: [
        'Everything in Monthly',
        'Save $10 per year',
        'Unlimited physical mailings',
        'Premium candidate research',
        'Election history tracking',
        'Priority support',
      ],
      cta: 'Start Free Trial',
      ctaVariant: 'secondary',
      popular: false,
    },
  ];

  return (
    <div className="bg-neutral-50 min-h-screen py-12">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4">Choose Your Plan</h1>
          <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
            Start free and upgrade anytime to access premium features like physical mail voter
            guides and advanced reminders.
          </p>
        </div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan) => (
            <Card
              key={plan.name}
              patrioticShadow
              hover
              className={`relative ${plan.popular ? 'border-2 border-primary-500' : ''}`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-primary-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </span>
                </div>
              )}

              <CardHeader>
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-primary-600">{plan.price}</span>
                  <span className="text-neutral-600 ml-2">{plan.period}</span>
                </div>
                <p className="text-neutral-600 mt-2">{plan.description}</p>
              </CardHeader>

              <CardContent>
                <ul className="space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <svg
                        className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-neutral-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter>
                <Button
                  variant={plan.ctaVariant}
                  size="lg"
                  fullWidth
                  onClick={() => navigate('/signup')}
                >
                  {plan.cta}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* FAQ */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-2xl font-serif font-bold text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Can I cancel anytime?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-neutral-600">
                  Yes! You can cancel your subscription at any time. Your access continues until the
                  end of your billing period.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">What payment methods do you accept?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-neutral-600">
                  We accept all major credit cards (Visa, Mastercard, American Express, Discover)
                  through our secure payment processor, Stripe.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Is my data secure?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-neutral-600">
                  Absolutely. We use industry-standard encryption, are GDPR and CCPA compliant, and
                  never share your personal information with third parties.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
